import ENV from "../env";
import { buildHeaders, handleExpiredLogin } from "./utils";

/** Mirrors the backend's WorkoutResponse; nulls are serialized, so nullable fields stay nullable here. */
export type Workout = {
    id: string;
    workoutTitle: string;
    topics: WorkoutTopic[];
    lessons: string[];
    tags: string[];
    questions: WorkoutQuestion[];
    users: WorkoutUser[];
    currentUser: WorkoutUser | null;
};

export type WorkoutTopic = {
    id: string;
    name: string;
};

export type WorkoutQuestion = {
    id: string;
    topicTitle: string;
    text: string;
    hint: string;
    ref: string[];
    options: string[];
    correct: number;
    answers: WorkoutAnswer[];
};

export type WorkoutAnswer = {
    user: string;
    index: number;
};

export type WorkoutUser = {
    id: string;
    name: string;
    lives: number | null;
    success: boolean;
    finished: boolean;
    finishedDate: string | null;
};

/** Mirrors the backend's WorkoutsListResponse. */
export type WorkoutsListEntry = {
    id: string;
    workoutTitle: string;
    topics: WorkoutTopic[];
    created: string | null;
    users: WorkoutsListUser[];
};

export type WorkoutsListUser = {
    id: string;
    name: string;
    finished: boolean;
};

export default class WorkoutService {
    /**
     * `topics` names the question topics to draw from and `lessons` the lessons to verify; one of the
     * two is required. `courseId` is the course a verification session belongs to, so the backend can
     * record the result in the user's progress.
     */
    static startNewWorkout(workoutTitle: string,
                           targetAmount: number,
                           topics: string[],
                           lessons: string[] = [],
                           tags: string[] = [],
                           verify: number | null = null,
                           courseId: string | null = null): Promise<Workout> {

        return fetch(`${ ENV.BACKEND }/workout/start-workout`, {
            method: 'POST',
            headers: buildHeaders(),
            body: JSON.stringify({
                workoutTitle, targetAmount, topics, lessons, tags, courseId, verifySessionIndex: verify
            }),
        })
            .then((response) => handleExpiredLogin(response).json());
    }

    static submitAnswer(workoutId: string, questionId: string, answerIndex: number): Promise<Workout> {
        return fetch(`${ ENV.BACKEND }/workout/submit-answer`, {
            method: 'POST',
            headers: buildHeaders(),
            body: JSON.stringify({ workoutId, questionId, answerIndex }),
        })
            .then(response => handleExpiredLogin(response).json());
    }

    static joinWorkout(workoutId: string): Promise<Workout> {
        return fetch(`${ ENV.BACKEND }/workout/join-workout`, {
            method: 'POST',
            headers: buildHeaders(),
            body: JSON.stringify({ workoutId }),
        })
            .then((response) => handleExpiredLogin(response).json());
    }

    static getWorkout(workoutId: string): Promise<Workout> {
        return fetch(`${ ENV.BACKEND }/workout/${ workoutId }`, {
            method: 'GET',
            headers: buildHeaders()
        })
            .then((response) => handleExpiredLogin(response).json());
    }

    static getWorkouts(): Promise<WorkoutsListEntry[]> {
        return fetch(`${ ENV.BACKEND }/workout`, {
            method: 'GET',
            headers: buildHeaders()
        })
            .then((response) => handleExpiredLogin(response).json());
    }

    static finishWorkout(workoutId: string): Promise<Workout> {
        return fetch(`${ ENV.BACKEND }/workout/finish-workout`, {
            method: 'POST',
            headers: buildHeaders(),
            body: JSON.stringify({ workoutId }),
        })
            .then(response => handleExpiredLogin(response).json());
    }
}
